import { useRef } from 'react'
import { Link, useNavigate, useParams, useLocation } from 'react-router-dom'
import { useFetchProduct } from 'hooks/useFetchProduct'
import { getProductIdBySlug } from 'utils/productSlugCache'
import { makeNumbersFarsi } from 'utils/farsi'
import { Helmet } from 'react-helmet-async'
import { FullPageSpinner } from 'ui/FullPageSpinner'
import { PurchasePanel } from './components/PurchasePanel'
import { StarRating } from './components/StarRating'
import { InfoBlock } from './components/InfoBlock'
import { ReviewCard } from './components/CommentCard'
import { ReviewForm } from './components/CommentForm'
import { useAuth } from 'context/auth-context'
import { ExpandableSection } from './components/ExpandableSection'
import { ImageSlider } from './components/ImageSlider'
import { getImageUrl, getImageAlt } from 'utils/imageUtils'

export const ProductDetails: React.FC = () => {
  const { productSlug } = useParams()
  const location = useLocation()
  const detailsRef = useRef<HTMLDivElement>(null)
  const reviewsRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()
  const { user } = useAuth()

  const productId =
    (location.state as { productId?: string } | null)?.productId ??
    (productSlug ? getProductIdBySlug(productSlug) : undefined)

  const { book, error, isLoading } = useFetchProduct(productId)

  const handleBackButtonClick = () => {
    navigate('/')
  }

  const handleScrollToDetails = (
    event: React.MouseEvent<HTMLAnchorElement>,
  ) => {
    event.preventDefault()
    detailsRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  const handleScrollToReviews = (
    event: React.MouseEvent<HTMLAnchorElement>,
  ) => {
    event.preventDefault()
    detailsRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  return (
    <div className='w-full min-h-screen' style={{ backgroundColor: 'var(--bg-secondary)' }}>
      <Helmet>
        <title className='text-xl font-semibold font-semibold'>
          {` جزئیات کتاب ${book?.title}`}
        </title>
        <meta
          name='description'
          content='بوک استوری - هر کتاب یک داستان، هر داستان یک زندگی دوباره'
        />
        {book?.images?.[0] && (
          <meta
            property='og:image'
            content={getImageUrl(book.images[0].image)}
          />
        )}
        {book?.images?.[0] && (
          <meta
            property='og:image:alt'
            content={getImageAlt(book.images[0].alt, book.title, 0)}
          />
        )}
        {book?.images?.[0] && (
          <meta
            name='twitter:image'
            content={getImageUrl(book.images[0].image)}
          />
        )}
        {book?.images?.[0] && (
          <meta
            name='twitter:image:alt'
            content={getImageAlt(book.images[0].alt, book.title, 0)}
          />
        )}
      </Helmet>

      {error && <h3>{error.message}</h3>}
      {isLoading && <FullPageSpinner />}

      <main className='container flex flex-col mx-auto px-4 py-8 md:py-12'>
        <button
          className='rounded border-none w-full md:w-1/4 my-4 p-2 text-white cursor-pointer lg:hidden'
          style={{ backgroundColor: 'var(--accent-primary)' }}
          onClick={handleBackButtonClick}
        >
          بازگشت به صفحه اصلی
        </button>
        <div className='flex flex-col lg:flex-row-reverse lg:space-x-16 my-8'>
          <div className='w-full lg:w-1/3 mt-0 lg:mt-0'>
            {book && <PurchasePanel book={book} />}
            
            <div
              ref={detailsRef}
              id='details'
              className='mt-6 p-6 rounded-xl shadow-sm scroll-mt-20 w-full lg:w-96'
              style={{ backgroundColor: 'var(--card-bg)' }}
            >
              <h2 className='text-xl font-semibold mb-4' style={{ color: 'var(--text-primary)' }}>
                جزئیات
              </h2>
              <div className='grid grid-cols-1 gap-y-0 text-sm'>
                <InfoBlock label='ناشر' value={book?.vendor} />
                <InfoBlock
                  label='سال انتشار'
                  value={makeNumbersFarsi(book?.attributes.publishYear)}
                />
                <InfoBlock label='مترجم' value={book?.attributes.translator} />
                <InfoBlock label='دسته بندی' value={book?.categoryTitle} />
                <InfoBlock label='موضوع' value={book?.subject} />
                <InfoBlock label='نوع جلد' value={book?.cover} />
                <InfoBlock
                  label='شابک کتاب'
                  value={makeNumbersFarsi(book?.barcode)}
                />
                <InfoBlock label='قطع' value={book?.format} />
                <InfoBlock
                  label='تعداد صفحات'
                  value={makeNumbersFarsi(book?.attributes.pagesNumber)}
                />
              </div>
            </div>
          </div>
          <div className='w-full lg:w-2/3'>
            <div className='flex flex-col md:flex-row md:space-x-8 md:space-x-reverse'>
              {/* <div className='w-full md:w-1/3 flex-shrink-0'>
                <img
                  src={`${API_BASE_URL}/files/${book?.images[0]}`}
                  alt={`جلد کتاب ${book?.title}`}
                  className='rounded-lg shadow-lg w-full object-cover'
                />
              </div> */}
              <div className='w-full md:w-1/3 flex-shrink-0'>
                <ImageSlider
                  images={book?.images || []}
                  title={book?.title || ''}
                  // autoPlay
                />
              </div>
              <div className='mt-6 md:mt-0 flex-1'>
                <h1 className='text-3xl md:text-4xl font-bold leading-tight' style={{ color: 'var(--text-primary)' }}>
                  {book?.title}
                </h1>
                <p className='text-xl mt-4' style={{ color: 'var(--text-secondary)' }}>
                  نویسنده:{' '}
                  <span className='font-semibold'>
                    {book?.attributes.author}
                  </span>
                </p>

                <div className='flex items-center mt-4 space-x-2 space-x-reverse'>
                  <StarRating rating={book?.averageRating as number} />
                  <span style={{ color: 'var(--text-secondary)' }}>
                    {makeNumbersFarsi(book?.averageRating)}
                  </span>
                  <a
                    href='#reviews'
                    onClick={handleScrollToReviews}
                    className='inline-block text-base hover:underline'
                    style={{ color: 'var(--accent-primary)' }}
                  >
                    ({makeNumbersFarsi(book?.approvedReviewCount)} نظر)
                  </a>
                </div>

                <div className='mt-4 pt-4 border-t text-sm space-y-1' style={{ color: 'var(--text-secondary)', borderColor: 'var(--border-color)' }}>
                  {book?.attributes?.translator &&
                    book?.attributes?.translator?.length > 0 && (
                      <p className='text-base font-normal'>
                        مترجم:{' '}
                        <span className='text-lg font-medium' style={{ color: 'var(--text-primary)' }}>
                          {book?.attributes.translator}
                        </span>
                      </p>
                    )}
                  <p className='text-base font-normal'>
                    ناشر:{' '}
                    <span className='text-lg font-medium' style={{ color: 'var(--text-primary)' }}>
                      {book?.vendor}
                    </span>
                  </p>
                  <p className='text-base font-normal'>
                    موضوع:{' '}
                    <span className='text-lg font-medium' style={{ color: 'var(--text-primary)' }}>
                      {book?.subject}
                    </span>
                  </p>
                  <p className='text-base font-normal'>
                    دسته بندی:{' '}
                    <span className='text-lg font-medium' style={{ color: 'var(--text-primary)' }}>
                      {book?.categoryTitle}
                    </span>
                  </p>
                </div>
                {/* <a
                  href='#details'
                  onClick={handleScrollToDetails}
                  className='inline-block mt-3 text-base hover:underline'
                  style={{ color: 'var(--accent-primary)' }}
                >
                  نمایش جزئیات بیشتر...
                </a> */}
              </div>
            </div>

            {book?.description && (
              <ExpandableSection
                title='درباره این کتاب'
                content={book?.description}
              />
            )}
            {/* {book?.extraDescription && (
              <ExpandableSection
                title='توضیحات تکمیلی'
                content={book?.extraDescription}
              />
            )} */}
            {book?.fromTheBackCover && (
              <ExpandableSection
                title='از پشت جلد کتاب'
                content={book?.fromTheBackCover}
              />
            )}
            {book?.partOfBook && (
              <ExpandableSection
                title='برشی از کتاب'
                content={book?.partOfBook}
              />
            )}
            {book?.someSentencesFromBook &&
              book?.someSentencesFromBook?.length > 0 && (
                <ExpandableSection
                  title='جملاتی از کتاب'
                  content={book?.someSentencesFromBook}
                  isList={true}
                />
              )}
            {book?.aboutTheAuthor && (
              <ExpandableSection
                title='درباره نویسنده'
                content={book?.aboutTheAuthor}
              />
            )}



            <div
              ref={reviewsRef}
              id='reviews'
              className='mt-12 p-6 rounded-xl shadow-sm'
              style={{ backgroundColor: 'var(--card-bg)' }}
            >
              <h2 className='text-2xl font-bold' style={{ color: 'var(--text-primary)' }}>
                امتیازها و نظرات
              </h2>
              {book?.reviews && book?.reviews?.length > 0 ? (
                <div className='mt-4'>
                  {book?.reviews?.map((review) => (
                    <ReviewCard key={review.id} review={review} />
                  ))}
                </div>
              ) : (
                <div className='flex flex-col mt-4'>
                  <p className='text-base font-medium py-2'>
                    هنوز نظری برای این کتاب ثبت نشده است. اولین نفر باشید!
                  </p>
                </div>
              )}
              {user ? (
                <ReviewForm productId={productId} />
              ) : (
                <div className='flex flex-col mt-4'>
                  <p className='text-base font-medium py-2'>
                    برای ثبت امتیاز و نظر، لطفا وارد سایت شوید.
                  </p>
                  <Link
                    to='/login'
                    className='mt-4 w-full text-center md:w-1/3 xl:w-1/5 px-6 py-3 text-white font-semibold rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-300 transform hover:scale-105'
                    style={{ backgroundColor: 'var(--accent-primary)' }}
                  >
                    ورود / ثبت نام
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* <div className='flex flex-col items-start mt-14 h-56 border rounded-md p-4'>
        <h1 className='text-xl text-center'>کتابهای مشابه</h1>
        <p>{product?.introduce}</p>
      </div>
      <div className='flex flex-col items-start mt-14 h-56 border rounded-md p-4'>
        <h1 className='text-xl text-center'>کتابهای دیگر از این ناشر</h1>
        <p>{product?.introduce}</p>
      </div>
      <div className='flex flex-col items-start mt-14 h-56 border rounded-md p-4'>
        <h1 className='text-xl text-center'>کتابهای دیگر از این نویسنده</h1>
        <p>{product?.introduce}</p>
      </div>
      */}
    </div>
  )
}
